import { FORECAST_SCENARIO_ID_KEY, VALUE_KEY, YEAR_KEY } from '@portal/common/models/data-key';
import { DataFilters, DataKey, DataRecord, DataResponse, Organization } from '@portal/common/types';

import config from '../../../../config';
import { loadData } from '../../../../utility/data-loader';

const eq = (a: any, b: any) => String(a) === String(b);

type ColIdx = {
    year: number;
    scenario: number;
    value: number;
};

const getColIdx = (columns: Array<DataKey>): ColIdx | null => {
    const year = columns.indexOf(YEAR_KEY);
    const scenario = columns.indexOf(FORECAST_SCENARIO_ID_KEY);
    const value = columns.indexOf(VALUE_KEY);
    if ([year, scenario, value].some((i) => i === -1)) return null;
    return { year, scenario, value };
};

export const alignRecordToColumns = (
    record: Array<any>,
    fromColumns: Array<DataKey>,
    toColumns: Array<DataKey>
): Array<any> => {
    if (
        fromColumns.length === toColumns.length &&
        fromColumns.every((k, i) => k === toColumns[i])
    ) {
        return record;
    }
    const pos: Record<string, number> = {};
    fromColumns.forEach((k, i) => {
        pos[String(k)] = i;
    });
    return toColumns.map((k) => {
        const i = pos[String(k)];
        return i != null ? record[i] : null;
    });
};

export const alignRecordsToColumns = (
    records: Array<DataRecord>,
    fromColumns: Array<DataKey>,
    toColumns: Array<DataKey>
) => records.map((record) => alignRecordToColumns(record, fromColumns, toColumns));

export const canPerformDengueCalculations = ({
    records,
    columns,
}: {
    records: Array<DataRecord>;
    columns: Array<DataKey>;
}): boolean => {
    const idx = getColIdx(columns);
    if (!idx) return false;

    const baselineScenarioId = config.dengueBaselineForecastScenarioId;
    const uniqScenarios = Array.from(new Set(records.map((r) => r[idx.scenario])));
    const hasBaseline = uniqScenarios.some((s) => eq(s, baselineScenarioId));
    const hasInterventions = uniqScenarios.some((s) => !eq(s, baselineScenarioId));

    return hasBaseline && hasInterventions;
};

export const fetchBaselineScenarioData = async ({
    organization,
    dataCollection,
    conditions,
    currentFilters,
    granularity,
}: {
    organization: Organization;
    dataCollection: any;
    conditions: any[];
    currentFilters: DataFilters;
    granularity: any;
}): Promise<Record<string, DataResponse>> => {
    const baselineScenarioId = config.dengueBaselineForecastScenarioId;
    const baselineFilters = {
        ...currentFilters,
        [FORECAST_SCENARIO_ID_KEY]: [baselineScenarioId],
    };

    const dataLoader = loadData(
        organization,
        dataCollection,
        conditions,
        baselineFilters,
        granularity
    );

    return await dataLoader(baselineFilters);
};

export const getUniqueScenarios = (dataResponse: DataResponse): any[] => {
    const scenarioIdx = dataResponse.columns.indexOf(FORECAST_SCENARIO_ID_KEY);
    if (scenarioIdx === -1) return [];

    return [...new Set(dataResponse.records.map((r) => r[scenarioIdx]))];
};

export const areAvertedCalculationsEnabled = (
    uniqueScenarios: any[],
    organizationId?: string
): boolean => {
    const baselineScenarioId = config.dengueBaselineForecastScenarioId;
    const hasBaseline = uniqueScenarios.includes(baselineScenarioId);
    const hasInterventions = uniqueScenarios.some((s) => s !== baselineScenarioId);
    const canCalculateNow = hasBaseline && hasInterventions;
    const canFetchBaseline = !hasBaseline && hasInterventions && !!organizationId;

    return canCalculateNow || canFetchBaseline;
};

export const areCumulativeCalculationsEnabled = (uniqueScenarios: any[]): boolean => {
    return uniqueScenarios.length > 0;
};

export const isOnlyBaselineSelected = (selectedScenarios: number[]): boolean => {
    const baselineScenarioId = config.dengueBaselineForecastScenarioId;
    return selectedScenarios.length === 1 && selectedScenarios[0] === baselineScenarioId;
};

export const hasBaselineScenario = (uniqueScenarios: any[]): boolean => {
    const baselineScenarioId = config.dengueBaselineForecastScenarioId;
    return uniqueScenarios.includes(baselineScenarioId);
};

export const chartRequestIncludesDengueBaselineScenario = (
    chartDataRequestFilters: Record<string, unknown> | null | undefined
): boolean => {
    const raw = chartDataRequestFilters?.[FORECAST_SCENARIO_ID_KEY];
    if (raw === undefined || raw === null || raw === '') {
        return false;
    }
    const values = Array.isArray(raw) ? raw : [raw];
    const baselineId = config.dengueBaselineForecastScenarioId;
    return values.some((v) => String(v) === String(baselineId));
};

export const mergedChartDataResponseIncludesDengueBaseline = (
    mergedChartDataResponse: DataResponse | null | undefined
): boolean => {
    if (!mergedChartDataResponse?.records?.length) {
        return false;
    }
    return hasBaselineScenario(getUniqueScenarios(mergedChartDataResponse));
};

export const mergeBaselineWithCurrentData = ({
    currentData,
    baselineData,
}: {
    currentData: Record<string, DataResponse>;
    baselineData: Record<string, DataResponse>;
}): Record<string, DataResponse> => {
    const merged: Record<string, DataResponse> = { ...currentData };

    Object.entries(baselineData).forEach(([dataType, baseResp]) => {
        const baseColumns = baseResp.columns;
        const baseRecords = baseResp.records;

        if (merged[dataType]) {
            const currentCols = merged[dataType].columns;
            const currentRecs = merged[dataType].records;

            const currentResponse: DataResponse = {
                columns: currentCols,
                records: currentRecs,
            };
            if (hasBaselineScenario(getUniqueScenarios(currentResponse))) {
                return;
            }

            const alignedBaseline = alignRecordsToColumns(baseRecords, baseColumns, currentCols);

            merged[dataType] = {
                columns: currentCols,
                records: [...currentRecs, ...alignedBaseline],
            };
        } else {
            merged[dataType] = baseResp;
        }
    });

    return merged;
};
